interface GlyphTileProps {
  glyph: string;
  state?: "idle" | "correct" | "wrong" | "selected";
  size?: "sm" | "md" | "lg";
  onClick?: () => void;
  disabled?: boolean;
}

const STATES: Record<NonNullable<GlyphTileProps["state"]>, string> = {
  idle: "border-rule bg-paper text-ink",
  // Correct — a sindoor mark, the way a right answer is ticked in red.
  correct: "border-sindoor bg-paper text-sindoor",
  // Wrong — struck through in muted ink, dimmed.
  wrong: "border-ink-soft bg-paper-deep text-ink-soft line-through",
  // Selected — a solid ink fill, chosen but not yet judged.
  selected: "border-ink bg-ink text-paper",
};

const SIZES: Record<NonNullable<GlyphTileProps["size"]>, string> = {
  sm: "h-12 w-12 text-2xl",
  md: "h-16 w-16 text-3xl",
  lg: "h-24 w-24 text-5xl",
};

/**
 * GlyphTile — a single Devanagari character on a bordered paper tile.
 *
 * The atom of every drill and lesson: an akshara set in Tiro
 * Devanagari, framed by a hairline rule. State is carried by ink alone
 * (sindoor for correct, muted ink for wrong, solid ink for selected) —
 * no color glow, no shadow.
 */
export function GlyphTile({
  glyph,
  state = "idle",
  size = "md",
  onClick,
  disabled = false,
}: GlyphTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      lang="hi"
      className={`inline-flex items-center justify-center border font-dev leading-none transition-colors duration-150 disabled:cursor-not-allowed ${STATES[state]} ${SIZES[size]}`}
    >
      {glyph}
    </button>
  );
}
